import {
    ResponsiveContainer,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    Label,
    ComposedChart,
    Cell
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { AgrochemicalsTooltip, formatGraphAxis, formatTicker } from '../utils/graph-utils';
import { composedChartMargin, barGraphFontSize, labelOffSet, legendHeight, responsiveContainerHeight, groupColors, fertilizerBarSize, chartOffset } from '../constants/graph-constants';

/**
 * 
 * @param {Object} props 
 * @param {{category: string, total: number, type: string}[]} props.data Graph data from backend
 * @param {string} props.type
 * @param {string} props.group "Twee Jonge Gezellen"
 * @param {string} props.year "2024"
 * @param {string} props.unit This argument populates the Y axis with the corresponding unit value passed here.
 * @returns {JSX.Element} The facility's bar graph which displays an array of fertilizer items
 */

export default function AgrochemicalsGroupBarGraph({ data, type, year, unit, companyName }) {
    const legendItems = data?.map(item => ({
        value: item.category,
        color: groupColors[item.category] || '#ccc'
    }));
    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <Typography variant="h6" align="center" gutterBottom>
                {companyName} {type} {year} per Group
            </Typography>
            <ResponsiveContainer width="100%" height={responsiveContainerHeight}>
                <ComposedChart data={data} margin={composedChartMargin}>
                    <CartesianGrid stroke="#f5f5f5" />
                    <XAxis
                        dataKey="category"
                        fontSize={barGraphFontSize}
                    >
                        <Label value="Group" position='insideBottom' offset={labelOffSet} fontSize={barGraphFontSize} />
                    </XAxis>
                    <YAxis
                        fontSize={barGraphFontSize}
                        domain={([dataMin, dataMax]) => { const yAxisDisplayMax = formatGraphAxis(dataMax, dataMin); return yAxisDisplayMax }}
                        tickFormatter={value => formatTicker(value)}
                    >
                        <Label value={unit} angle={-90} position={'insideLeft'} offset={chartOffset} fontSize={barGraphFontSize} />
                    </YAxis>
                    <Tooltip content={<AgrochemicalsTooltip variant='group' />} />
                    <Legend content={<AgrochemicalGroupLegend legendItems={legendItems} />} verticalAlign='top' height={legendHeight} />
                    <Bar
                        dataKey={'total'}
                        name={'category'}
                        barSize={fertilizerBarSize}
                    >
                        {data?.map((entry, index) => (
                            <Cell
                                key={index}
                                fill={groupColors[entry.category] || '#ccc'}
                            />
                        ))
                        }
                    </Bar>
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
}

function AgrochemicalGroupLegend({ legendItems }) {
    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
            {legendItems?.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 14, height: 14, bgcolor: item.color, borderRadius: '4px' }} />
                    <Typography sx={{ color: item.color }}>{item.value}</Typography>
                </Box>
            ))}
        </Box>
    );
}